import { useEffect, useRef, useCallback } from 'react'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import { ChatMessage } from './aiAssistantModel'
import { LookupQuery, LookupResult } from './aiAssistantApi'
import { RatingState, initialRatingState } from './messageRatingModel'
import { getMessageDisplayState } from './chatWindowModel'
import { COMPACT_TOOL_CALLS_FEATURE } from './messageBubbleModel'
import MessageBubble from './MessageBubble'
import ChatWelcome from './ChatWelcome'

type Props = {
  messages: ChatMessage[]
  isStreaming: boolean
  fullScreen: boolean
  onSendMessage: (text: string) => void
  onDecide: (toolCallId: string, approved: boolean) => void
  onGroupDecide: (groupId: string, approved: boolean) => void
  onSubmitForm: (formId: string, values: Record<string, unknown>) => void
  onSubmitQuestionSet: (questionSetId: string, answers: Record<string, unknown>) => void
  onLookupSearch: (query: LookupQuery) => Promise<LookupResult>
  onLookupCreateRequest: (entity: string, query: string) => void
  disabled: boolean
  ratings: Map<string, RatingState>
  pendingRatings: Set<string>
  onRateMessage: (message: ChatMessage, thumbsUp: boolean) => void
  onSubmitMessageRationale: (message: ChatMessage, text: string) => void
}

const ChatWindow = ({
  messages,
  isStreaming,
  fullScreen,
  onSendMessage,
  onDecide,
  onGroupDecide,
  onSubmitForm,
  onSubmitQuestionSet,
  onLookupSearch,
  onLookupCreateRequest,
  disabled,
  ratings,
  pendingRatings,
  onRateMessage,
  onSubmitMessageRationale,
}: Props) => {
  const compactToolCalls = useFeatureFlag(COMPACT_TOOL_CALLS_FEATURE)
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)

  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const threshold = 100
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
  }, [])

  useEffect(() => {
    if (isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  const horizontalPadding = fullScreen ? 100 : 16

  if (messages.length === 0) {
    return (
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `0 ${horizontalPadding}px`,
      }}>
        <ChatWelcome onSendMessage={onSendMessage} disabled={disabled} />
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: `16px ${horizontalPadding}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ maxWidth: 1440, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((msg, i) => {
          const display = getMessageDisplayState(msg, i === messages.length - 1, isStreaming)
          return (
            <MessageBubble
              key={msg.id}
              message={msg}
              isStreaming={display.isStreaming}
              compactToolCalls={compactToolCalls}
              onSendMessage={onSendMessage}
              onDecide={onDecide}
              onGroupDecide={onGroupDecide}
              onSubmitForm={onSubmitForm}
              onSubmitQuestionSet={onSubmitQuestionSet}
              onLookupSearch={onLookupSearch}
              onLookupCreateRequest={onLookupCreateRequest}
              optionsInteractive={display.optionsInteractive && !disabled}
              rating={
                display.showRating
                  ? {
                      state: ratings.get(msg.id) ?? initialRatingState,
                      pending: pendingRatings.has(msg.id),
                      onRate: (thumbsUp) => onRateMessage(msg, thumbsUp),
                      onSubmitRationale: (text) => onSubmitMessageRationale(msg, text),
                    }
                  : undefined
              }
            />
          )
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}

export default ChatWindow
